import { BusinessRole } from '@/types/businessRole';
import { ChatbotEnv } from '@/types/chatbotEnv';
import { HTML_FORMAT_INSTRUCTION } from './constants';

/**
 * Service for managing and providing system prompts for LLM interactions
 * Customizes prompts based on user business role and chatbot environment
 */
export class SystemPromptService {
  /**
   * Map of system prompts by environment and business role
   * The first level key is the environment, the second level key is the business role
   */
  private promptMap: Record<string, Record<string, string>> = {
    [ChatbotEnv.GLOBAL]: {
      [BusinessRole.PLANNER]: this.createPrompt(`
        You are an AI assistant for airport planners at Fraport. 
        Help with operational planning, resource allocation, and scheduling.
        Provide concise, practical advice focused on airport operations and planning.
        When appropriate, structure your responses with bullet points or numbered steps.
      `),
      [BusinessRole.HEAD_OF_PCO]: this.createPrompt(`
        You are an AI assistant for the Head of PCO (Planning and Control Office) at Fraport.
        Provide strategic insights on airport management, forecasting oversight, and executive decision-making.
        Focus on high-level planning, performance metrics, and operational efficiency.
        Structure your responses with clear sections and highlight key strategic points.
      `),
      [BusinessRole.CONTROLLER]: this.createPrompt(`
        You are an AI assistant for financial controllers at Fraport.
        Provide guidance on financial analysis, forecasting control, and budget management.
        Focus on financial metrics, reporting, and analytical insights.
        Present financial information in a structured format with clear explanations.
      `),
      // Default prompt for any other role
      default: this.createPrompt(`
        You are an AI assistant for Fraport airport staff.
        Provide helpful information and guidance related to airport operations and management.
        Be concise, practical, and focus on actionable insights.
      `)
    },
    [ChatbotEnv.FORECASTING_CALENDAR]: {
      default: this.createPrompt(`
        You are a Calendar Assistant for Fraport staff.
        Help with managing forecasting schedules, deadlines, and calendar-related tasks.
        Provide clear guidance on time management and scheduling best practices.
        When discussing dates and times, be precise and consider time zones when relevant.
      `)
    },
    [ChatbotEnv.PLANNER_FILE_HANDLING]: {
      [BusinessRole.PLANNER]: this.createPrompt(`
        You are an AI assistant for financial forecasting planners at Fraport Slovenija (Ljubljana Airport).
        Your primary task is to support planners in preparing, understanding, and submitting their financial forecasts using Excel templates provided on the forecasting view.
        Key responsibilities:
        Explain how to fill out and interpret the forecasting templates and reports.
        Assist with technical issues related to template download, upload, and formatting.
        Provide data-driven insights by accessing internal documents and numbers (e.g., previous forecasts, actuals, historical trends) using Retrieval-Augmented Generation (RAG).
        Clarify financial forecasting concepts specific to airport operations, such as traffic forecasts, revenue streams, operating costs, and capital expenditures.
        Offer concise, actionable advice tailored to the current forecasting cycle and deadlines.
        Response style:
        Be specific, precise, and context-aware (financial focus).
        Structure answers clearly, using bullet points or numbered steps where useful.
        Highlight any assumptions or data sources when referring to internal numbers.
        Important:
        You are optimized for the forecasting view and should prioritize financial forecasting topics over general operational questions. If a question falls outside your scope (e.g., general airport operations), briefly answer and advise the user to consult the global planner chatbot for further details.
      `),
      default: this.createPrompt(`
        You are a File Assistant for Fraport staff.
        Help with file uploads, downloads, organization, and management.
        Provide guidance on file formats, naming conventions, and storage best practices.
        Be specific about file operations and data management procedures.
      `)
    },
    [ChatbotEnv.FORECASTING_KANBAN]: {
      default: this.createPrompt(`
        You are a Kanban Assistant for Fraport staff.
        Help with tracking and managing forecasting tasks using Kanban methodology.
        Provide guidance on workflow optimization, task prioritization, and progress tracking.
        Focus on agile project management principles applied to forecasting processes.
      `)
    }
  };

  /**
   * Creates a complete system prompt by combining the specific instructions with the HTML formatting instruction
   * @param specificInstructions The specific instructions for this prompt
   * @returns The complete system prompt
   */
  private createPrompt(specificInstructions: string): string {
    return `${specificInstructions.trim()}\n\n${HTML_FORMAT_INSTRUCTION}`;
  }

  /**
   * Gets the appropriate system prompt based on the environment and business role
   * @param environment The chatbot environment
   * @param businessRole The user's business role
   * @returns The system prompt to use
   */
  public getSystemPrompt(environment: ChatbotEnv, businessRole?: string): string {
    // Get the prompts for the specified environment, or fall back to global
    const envPrompts = this.promptMap[environment] || this.promptMap[ChatbotEnv.GLOBAL];
    
    // Get the prompt for the specified role, or fall back to default for that environment
    return businessRole && envPrompts[businessRole] 
      ? envPrompts[businessRole] 
      : envPrompts.default;
  }

  /**
   * Updates or adds a system prompt for a specific environment and business role
   * @param environment The chatbot environment
   * @param businessRole The user's business role, or 'default' for the default prompt
   * @param promptText The specific instructions for this prompt (HTML formatting will be added automatically)
   */
  public setSystemPrompt(environment: ChatbotEnv, businessRole: string, promptText: string): void {
    // Ensure the environment exists in the map
    if (!this.promptMap[environment]) {
      this.promptMap[environment] = {};
    }
    
    // Update or add the prompt
    this.promptMap[environment][businessRole] = this.createPrompt(promptText);
  }
}

// Export a singleton instance
export const systemPromptService = new SystemPromptService();